import React from 'react';

export type QuietBoundaryProps = {
  children: React.ReactNode;
  /**
   * A failed boundary tries its children again once this changes (the flow has moved on); a working one keeps them as
   * they are. Absent: failed for good (key the boundary by what it shows to give the next one a fresh try).
   */
  resetKey?: string;
  /** What shows in failed children's place: nothing by default. */
  fallback?: React.ReactNode;
  /** Told once the children have thrown (after React has reported it): the flow can move on past them. */
  onError?: () => void;
};

type QuietBoundaryState = { failed: boolean; resetKey: string | undefined };

/**
 * Keeps a failed FTUX part from taking the studio down with it: a chunk that cannot load (a dropped connection, a stale
 * hash after a deploy) or a part that throws renders its fallback (nothing by default) instead. React still reports
 * the error (console.error); the boundary only drops its children, until its `resetKey` moves on.
 */
export class QuietBoundary extends React.Component<QuietBoundaryProps, QuietBoundaryState> {
  state: QuietBoundaryState = { failed: false, resetKey: this.props.resetKey };

  static getDerivedStateFromError(): Partial<QuietBoundaryState> {
    return { failed: true };
  }

  static getDerivedStateFromProps(
    props: QuietBoundaryProps,
    state: QuietBoundaryState,
  ): Partial<QuietBoundaryState> | null {
    return props.resetKey === state.resetKey ? null : { failed: false, resetKey: props.resetKey };
  }

  componentDidCatch(): void {
    this.props.onError?.();
  }

  render(): React.ReactNode {
    return this.state.failed ? this.props.fallback ?? null : this.props.children;
  }
}

export default QuietBoundary;
